// src/components/landing/FeatureHighlights.tsx
// Three deep-dive feature cards that complement the higher-level
// "Why UnlimitedTests?" 4-card grid above. Each card here highlights a
// distinct, real feature in the product (no duplication, no claims).
'use client'

import { Repeat2, LineChart, CalendarCheck } from 'lucide-react'

type Feature = {
  icon: React.ReactNode
  iconBg: string
  title: string
  description: string
  bullets: string[]
}

const FEATURES: Feature[] = [
  {
    icon: <Repeat2 className="h-6 w-6 text-brand-ink" />,
    iconBg: 'bg-brand/15 ring-brand/30',
    title: 'Spaced repetition review',
    description:
      "Every question you miss becomes a review card. We resurface it at the right interval so it sticks — you're not just answering practice questions, you're building memory.",
    bullets: [
      'Auto-built from your wrong answers',
      'Re-tested at increasing intervals',
      "Drills retire once you've mastered them",
    ],
  },
  {
    icon: <LineChart className="h-6 w-6 text-brand-ink" />,
    iconBg: 'bg-brand/15 ring-brand/30',
    title: 'Score prediction',
    description:
      "See roughly where you'd score today. Our estimate is shaped on College Board's published practice-test scoring and updates as you practice — a grounded estimate with a range, not an official score.",
    bullets: [
      'Live forecast toward your target',
      'Shaped on published practice-test scoring',
      'Updates after every full-length test',
    ],
  },
  {
    icon: <CalendarCheck className="h-6 w-6 text-emerald-700 dark:text-emerald-300" />,
    iconBg: 'bg-emerald-500/15 ring-emerald-500/30',
    title: 'Adaptive study plan',
    description:
      'Tell us your target score and test date. We build a daily plan focused on your weakest topics and adjust it as your skills shift across modules.',
    bullets: [
      'Built around your test date',
      'Re-prioritizes weak topics weekly',
      'Mixes timed drills, full tests, and review',
    ],
  },
]

export default function FeatureHighlights() {
  return (
    <section className="px-5 py-14 md:px-10">
      <div className="mx-auto max-w-7xl">
        <h2 className="mb-3 text-center text-2xl font-semibold text-foreground md:text-3xl">
          More than <span className="text-brand-ink">practice questions</span>
        </h2>
        <p className="mb-10 text-center text-sm text-muted-foreground">
          What's actually in the toolkit, beyond the test engine.
        </p>
        <div className="grid gap-5 md:grid-cols-3">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-border bg-card/60 p-6 transition hover:border-brand/40 hover:bg-card/80"
            >
              <div
                className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full ring-1 ${f.iconBg}`}
              >
                {f.icon}
              </div>
              <h3 className="text-base font-semibold text-foreground">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.description}</p>
              <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                {f.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
